// src/components/JobSearchTimeline.tsx

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Group } from '@visx/group';
import { Grid } from '@visx/grid';
import { Zoom } from '@visx/zoom';
import { scaleTime, scaleLinear } from '@visx/scale';
import { ScaleTime, ScaleLinear } from 'd3-scale';
import { format, startOfDay, endOfDay, isWeekend, isFirstDayOfMonth, addDays, subDays, parseISO, differenceInDays } from 'date-fns';
import { JobApplicationEnhanced, ApplicationStatus } from '../types/jobSearch';
import { statusConfig } from '../utils/jobApplicationUtils';
import { IoRemoveOutline, IoAddOutline, IoChevronBackOutline, IoTodayOutline, IoChevronForwardOutline, IoLocationOutline, IoCalendarOutline } from 'react-icons/io5';
import { useDarkMode } from '../context/DarkModeContext';
import { Popover } from '@headlessui/react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import useUserTimezone from '../hooks/useUserTimezone';

interface TimelineProps {
    applications: JobApplicationEnhanced[];
    onApplicationClick: (application: JobApplicationEnhanced) => void;
    selectedApplications: string[];
    setSelectedApplications: (ids: string[]) => void;
}

interface StatusBar {
    id: string;
    startX: number;
    endX: number;
    status: ApplicationStatus;
}

interface GestureEvent extends Event {
    scale: number;
    rotation: number;
}

const DETAILS_COLUMN_WIDTH = 180;
const ROW_HEIGHT = 80;
const PADDING = 20;
const ZOOM_STEP = 1.2;
const DATE_PADDING_DAYS = 14; // Two weeks padding on each side
const HEADER_HEIGHT = 32;

const JobSearchTimeline: React.FC<TimelineProps> = ({
    applications,
    onApplicationClick,
    selectedApplications,
    setSelectedApplications,
}) => {
    const { darkMode } = useDarkMode();
    const { formatDateOnlyWithTimezone } = useUserTimezone();
    const [timelineOffset, setTimelineOffset] = useState(0);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Format date in the user's timezone but with a specific format
    const formatDateWithTimezone = (date: Date | string, formatStr?: string): string => {
        if (formatStr) {
            // For specific formats, we still use date-fns but making it timezone-aware would be better
            return format(date instanceof Date ? date : new Date(date), formatStr);
        }
        return formatDateOnlyWithTimezone(date);
    };

    // Update window width on resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const width = windowWidth - DETAILS_COLUMN_WIDTH;
    const height = applications.length * ROW_HEIGHT;

    // Calculate total height including padding for the last row
    const totalHeight = useMemo(() => {
        return (applications.length * ROW_HEIGHT) + PADDING * 2;
    }, [applications.length]);

    // Calculate the actual available width for the timeline, accounting for all padding and margins
    const availableWidth = useMemo(() => {
        // For mobile view (less than 768px)
        if (windowWidth < 768) {
            return windowWidth - DETAILS_COLUMN_WIDTH - 32; // Just account for basic padding
        }
        
        // For desktop view
        return windowWidth - DETAILS_COLUMN_WIDTH - 32 - (PADDING * 2);
    }, [windowWidth]);

    // Helper function to create a date in local timezone
    const createLocalDate = (dateStr: string | Date) => {
        if (dateStr instanceof Date) return dateStr;
        // For YYYY-MM-DD format or any other format, just create the date
        // This will interpret the date in local timezone
        return new Date(dateStr);
    };

    // Helper to create consistent dates for the status history
    const toConsistentDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return startOfDay(date);
    };

    // Calculate date range
    const dateRange = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (applications.length === 0) {
            // For empty applications, show 1 month before and after today
            return {
                start: subDays(today, 30),
                end: addDays(today, 30)
            };
        }

        const dates = applications.flatMap(app => [
            new Date(app.application_date.replace('Z', '')),
            ...(app.status_history || []).map(sh => new Date(sh.timestamp.replace('Z', '')))
        ]);

        // Include today in the range of dates
        dates.push(today);

        // Find min and max dates
        let minDate = dates.reduce((min, date) => date < min ? date : min);
        let maxDate = dates.reduce((max, date) => date > max ? date : max);

        // Ensure the range doesn't exceed 2 months
        const twoMonthsInMs = 1000 * 60 * 60 * 24 * 60; // 60 days
        const currentRange = maxDate.getTime() - minDate.getTime();
        
        if (currentRange > twoMonthsInMs) {
            // If range is too large, center it on the most recent activity
            const center = maxDate;
            minDate = new Date(center.getTime() - twoMonthsInMs/2);
            maxDate = new Date(center.getTime() + twoMonthsInMs/2);
        } else {
            // If range is smaller than 2 months, center it and expand to fill the window
            const center = new Date(minDate.getTime() + currentRange/2);
            const halfWindow = Math.min(twoMonthsInMs/2, currentRange * 2);
            minDate = new Date(center.getTime() - halfWindow);
            maxDate = new Date(center.getTime() + halfWindow);
        }

        // Set hours to start/end of day
        minDate.setHours(0, 0, 0, 0);
        maxDate.setHours(23, 59, 59, 999);
        
        return {
            start: minDate,
            end: maxDate
        };
    }, [applications]);

    // Create scales
    const xScale = useMemo(
        () =>
            scaleTime<number>({
                domain: [dateRange.start, dateRange.end],
                range: [PADDING, availableWidth - PADDING],
                nice: true
            }) as ScaleTime<number, number>,
        [dateRange, availableWidth]
    );

    const yScale = useMemo(
        () =>
            scaleLinear<number>({
                domain: [0, applications.length],
                range: [PADDING, applications.length * ROW_HEIGHT - PADDING],
                nice: true
            }) as ScaleLinear<number, number>,
        [applications.length]
    );

    // Calculate visible date range based on zoom and translation
    const getVisibleDateRange = useCallback((transform: { translateX: number; scaleX: number }) => {
        // Use availableWidth instead of calculating a new width
        const screenLeft = -transform.translateX / transform.scaleX;
        const screenRight = screenLeft + (availableWidth / transform.scaleX);

        return {
            start: xScale.invert(screenLeft),
            end: xScale.invert(screenRight)
        };
    }, [availableWidth, xScale]);

    // Helper function to determine if we should show a date label
    const shouldShowDateLabel = useCallback((date: Date, transform: { scaleX: number }) => {
        // Always show first day of month
        if (date.getDate() === 1) return true;
        
        // For other days, check the space between labels
        const pixelsPerDay = (availableWidth / 30) * transform.scaleX; // Assuming ~30 days visible
        
        // Show every 2nd day if we have enough space
        if (pixelsPerDay >= 60) return true;
        
        // Show every 3rd day if we have moderate space
        if (pixelsPerDay >= 40) return date.getDate() % 3 === 0;
        
        // Show every 5th day if we have less space
        if (pixelsPerDay >= 25) return date.getDate() % 5 === 0;
        
        // Show only every 7th day if space is tight
        return date.getDate() % 7 === 0;
    }, [availableWidth]);

    // Update renderGridLinesAndLabels to use the new shouldShowDateLabel function
    const renderGridLinesAndLabels = useCallback((transform: { translateX: number; scaleX: number }) => {
        const { start: visibleStart, end: visibleEnd } = getVisibleDateRange(transform);
        const lines = [];
        const labels = [];
        
        // Start at the beginning of the first visible day
        let currentDate = new Date(visibleStart);
        currentDate.setHours(0, 0, 0, 0);
        
        // End at the end of the last visible day
        const endDate = new Date(visibleEnd);
        endDate.setHours(23, 59, 59, 999);
        
        while (currentDate <= endDate) {
            const x = xScale(currentDate);
            const isMonth = isFirstDayOfMonth(currentDate);
            const transformedX = x * transform.scaleX;
            
            // Grid lines (inside zoom transform)
            lines.push(
                <line
                    key={currentDate.toISOString()}
                    x1={x}
                    x2={x}
                    y1={HEADER_HEIGHT}
                    y2={totalHeight}
                    stroke={darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}
                    strokeWidth={isMonth ? 2 : 1}
                    strokeDasharray={isWeekend(currentDate) ? "4,4" : undefined}
                />
            );
            
            // Date labels (outside zoom transform)
            if (isMonth || shouldShowDateLabel(currentDate, transform)) {
                if (isMonth) {
                    const isJanuary = currentDate.getMonth() === 0;
                    const monthFormat = isJanuary ? "MMM yyyy" : "MMM";
                    
                    labels.push(
                        <text
                            key={`month-${currentDate.toISOString()}`}
                            x={transformedX}
                            y={12}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className={`text-xs ${darkMode ? 'fill-gray-400' : 'fill-gray-600'} font-medium`}
                        >
                            {formatDateWithTimezone(currentDate, monthFormat)}
                        </text>
                    );
                }
                
                if (shouldShowDateLabel(currentDate, transform)) {
                    labels.push(
                        <text
                            key={`day-${currentDate.toISOString()}`}
                            x={transformedX}
                            y={26}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className={`text-xs ${darkMode ? 'fill-gray-400' : 'fill-gray-600'}`}
                        >
                            {formatDateWithTimezone(currentDate, "d")}
                        </text>
                    );
                }
            }
            
            currentDate = new Date(currentDate.getTime() + (24 * 60 * 60 * 1000));
        }
        
        return { lines, labels };
    }, [xScale, totalHeight, darkMode, shouldShowDateLabel]);

    const renderApplication = useCallback((
        app: JobApplicationEnhanced,
        index: number,
        xScale: ScaleTime<number, number>,
        yScale: ScaleLinear<number, number>
    ) => {
        const statusBars: StatusBar[] = [];
        const y = index * ROW_HEIGHT + HEADER_HEIGHT;
        const barHeight = ROW_HEIGHT * 0.25;

        // Sort status history by timestamp
        const sortedHistory = [...(app.status_history || [])]
            .sort((a, b) => {
                // Create dates at midnight for consistent alignment
                const dateA = new Date(a.timestamp.replace('Z', ''));
                dateA.setHours(0, 0, 0, 0);
                const dateB = new Date(b.timestamp.replace('Z', ''));
                dateB.setHours(0, 0, 0, 0);
                return dateA.getTime() - dateB.getTime();
            });

        // Render status bars
        let currentX = xScale((() => {
            const date = new Date(sortedHistory[0].timestamp.replace('Z', ''));
            date.setHours(0, 0, 0, 0);
            return date;
        })());

        sortedHistory.forEach((status, idx) => {
            const startX = currentX;
            const endX = idx < sortedHistory.length - 1
                ? xScale((() => {
                    const date = new Date(sortedHistory[idx + 1].timestamp.replace('Z', ''));
                    date.setHours(0, 0, 0, 0);
                    return date;
                })())
                : xScale(new Date());
            
            const date = new Date(status.timestamp.replace('Z', ''));
            date.setHours(0, 0, 0, 0);
            
            statusBars.push({
                id: `${app.id}-status-${idx}`,
                startX,
                endX,
                status: status.status
            });

            currentX = endX;
        });

        return statusBars.map(bar => {
            const statusIndex = parseInt(bar.id.split('-').pop() || '0');
            const statusDate = new Date(sortedHistory[statusIndex].timestamp.replace('Z', ''));
            statusDate.setHours(0, 0, 0, 0);
            
        return (
                <Group key={bar.id}>
                    <title>{`${statusConfig[bar.status].label} - ${formatDateWithTimezone(statusDate, 'MMM d, yyyy')}`}</title>
                    <rect
                        x={bar.startX}
                        y={y + (ROW_HEIGHT - barHeight)/2}
                        width={Math.max(2, bar.endX - bar.startX)}
                        height={barHeight}
                        fill={statusConfig[bar.status].color}
                        rx={2}
                        className="cursor-pointer hover:opacity-80"
                        onClick={() => onApplicationClick(app)}
                    />
                </Group>
            );
        });
    }, [onApplicationClick]);

    // Consolidate zoom validation logic
    const validateZoom = useCallback((transform: { translateX: number; scaleX: number }, newScale?: number) => {
        const visibleRange = getVisibleDateRange(transform);
        const visibleTimeMs = visibleRange.end.getTime() - visibleRange.start.getTime();
        const twoWeeksMs = 1000 * 60 * 60 * 24 * 14; // 14 days
        const twoMonthsMs = 1000 * 60 * 60 * 24 * 60; // 60 days

        // If checking a new scale
        if (newScale !== undefined) {
            const proposedVisibleTimeMs = visibleTimeMs * (transform.scaleX / newScale);
            // Return true if the proposed time window is valid
            return proposedVisibleTimeMs >= twoWeeksMs && proposedVisibleTimeMs <= twoMonthsMs;
        }

        // If checking current transform
        return visibleTimeMs >= twoWeeksMs && visibleTimeMs <= twoMonthsMs;
    }, [getVisibleDateRange]);

    return (
        <div className="flex flex-col h-full">
            {applications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64">
                    <p className="text-gray-500 mb-4">No applications yet</p>
                    <p className="text-sm text-gray-400">Add your first job application to see the timeline</p>
                </div>
            ) : (
                <div className="flex flex-col h-full">
                    <Zoom<SVGSVGElement>
                        width={availableWidth}
                        height={totalHeight}
                        scaleXMin={0.1}
                        scaleXMax={10}
                        scaleYMin={1}
                        scaleYMax={1}
                        constrain={(transform, prevTransform) => {
                            // If the transform would make the window invalid
                            if (!validateZoom(transform)) {
                                // Calculate the correct scale to maintain minimum/maximum window size
                                const visibleRange = getVisibleDateRange(transform);
                                const visibleTimeMs = visibleRange.end.getTime() - visibleRange.start.getTime();
                                const twoWeeksMs = 1000 * 60 * 60 * 24 * 14;
                                const twoMonthsMs = 1000 * 60 * 60 * 24 * 60;

                                let newScaleX = transform.scaleX;
                                if (visibleTimeMs < twoWeeksMs) {
                                    // Too zoomed in, adjust scale to show exactly 2 weeks
                                    newScaleX = transform.scaleX * (visibleTimeMs / twoWeeksMs);
                                } else if (visibleTimeMs > twoMonthsMs) {
                                    // Too zoomed out, adjust scale to show exactly 2 months
                                    newScaleX = transform.scaleX * (visibleTimeMs / twoMonthsMs);
                                }

                                return {
                                    ...transform,
                                    scaleX: newScaleX,
                                    translateY: 0,
                                    scaleY: 1,
                                    skewX: 0,
                                    skewY: 0
                                };
                            }

                            // Transform is valid, just ensure Y is locked
                            return {
                                ...transform,
                                translateY: 0,
                                scaleY: 1,
                                skewX: 0,
                                skewY: 0
                            };
                        }}
                    >
                        {(zoom) => {
                            const visibleRange = getVisibleDateRange(zoom.transformMatrix);
                            const dragStart = React.useRef({ x: 0, translateX: 0 });
                            const isDragging = React.useRef(false);

                            const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
                                isDragging.current = true;
                                dragStart.current = {
                                    x: 'touches' in e ? e.touches[0].clientX : e.clientX,
                                    translateX: zoom.transformMatrix.translateX
                                };
                                e.preventDefault();
                            }, [zoom.transformMatrix.translateX]);

                            const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
                                if (!isDragging.current) return;

                                const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
                                const dx = clientX - dragStart.current.x;
                                
                                // Scale the movement based on current zoom level and add damping
                                const movementDamping = 1.0; // Reduce movement speed
                                const zoomScaling = Math.max(0.2, Math.min(1, 1 / zoom.transformMatrix.scaleX)); // Scale movement based on zoom
                                const scaledDx = dx * movementDamping * zoomScaling;

                                zoom.setTransformMatrix({
                                    ...zoom.transformMatrix,
                                    translateX: dragStart.current.translateX + scaledDx,
                                    translateY: 0,
                                    scaleY: 1,
                                    skewX: 0,
                                    skewY: 0
                                });

                                e.preventDefault();
                            }, [zoom]);

                            const handleDragEnd = useCallback(() => {
                                isDragging.current = false;
                            }, []);

                            // Configure wheel zoom behavior
                            zoom.handleWheel = (event: WheelEvent | React.WheelEvent) => {
                                event.preventDefault();
                                
                                const currentScale = zoom.transformMatrix.scaleX;
                                const newScale = event.deltaY < 0 
                                    ? currentScale * ZOOM_STEP 
                                    : currentScale / ZOOM_STEP;

                                // Try to apply the new scale
                                if (validateZoom(zoom.transformMatrix, newScale)) {
                                    zoom.setTransformMatrix({
                                        ...zoom.transformMatrix,
                                        scaleX: newScale,
                                        scaleY: 1,
                                        skewX: 0,
                                        skewY: 0
                                    });
                                }
                            };

                            const handleZoomIn = useCallback(() => {
                                const currentScale = zoom.transformMatrix.scaleX;
                                const newScale = currentScale * ZOOM_STEP;

                                if (validateZoom(zoom.transformMatrix, newScale)) {
                                    // Get the current leftmost visible date
                                    const visibleRange = getVisibleDateRange(zoom.transformMatrix);
                                    const leftEdgeX = xScale(visibleRange.start);

                                    // Calculate new translation to maintain left edge position
                                    const newTranslateX = -leftEdgeX * newScale;

                                    zoom.setTransformMatrix({
                                        ...zoom.transformMatrix,
                                        scaleX: newScale,
                                        translateX: newTranslateX,
                                        translateY: 0,
                                        scaleY: 1,
                                        skewX: 0,
                                        skewY: 0
                                    });
                                }
                            }, [zoom, validateZoom, xScale, getVisibleDateRange]);

                            const handleZoomOut = useCallback(() => {
                                const currentScale = zoom.transformMatrix.scaleX;
                                const newScale = currentScale / ZOOM_STEP;

                                if (validateZoom(zoom.transformMatrix, newScale)) {
                                    // Get the current leftmost visible date
                                    const visibleRange = getVisibleDateRange(zoom.transformMatrix);
                                    const leftEdgeX = xScale(visibleRange.start);

                                    // Calculate new translation to maintain left edge position
                                    const newTranslateX = -leftEdgeX * newScale;

                                    zoom.setTransformMatrix({
                                        ...zoom.transformMatrix,
                                        scaleX: newScale,
                                        translateX: newTranslateX,
                                        translateY: 0,
                                        scaleY: 1,
                                        skewX: 0,
                                        skewY: 0
                                    });
                                }
                            }, [zoom, validateZoom, xScale, getVisibleDateRange]);

                            const handlePrevious = useCallback(() => {
                                zoom.setTransformMatrix({
                                    ...zoom.transformMatrix,
                                    translateX: zoom.transformMatrix.translateX + availableWidth / 2,
                                    translateY: 0,
                                    scaleY: 1,
                                    skewX: 0,
                                    skewY: 0
                                });
                            }, [zoom, availableWidth]);

                            const handleNext = useCallback(() => {
                                zoom.setTransformMatrix({
                                    ...zoom.transformMatrix,
                                    translateX: zoom.transformMatrix.translateX - availableWidth / 2,
                                    translateY: 0,
                                    scaleY: 1,
                                    skewX: 0,
                                    skewY: 0
                                });
                            }, [zoom, availableWidth]);

                            const handleToday = useCallback(() => {
                                const today = createLocalDate(new Date());
                                const todayX = xScale(today);
                                
                                // Calculate how many pixels represent one month at scale 1
                                const twoWeeksInMs = 1000 * 60 * 60 * 24 * 14; // 14 days
                                const startDate = createLocalDate(new Date(today.getTime() - twoWeeksInMs));
                                const endDate = createLocalDate(new Date(today.getTime() + twoWeeksInMs));
                                const totalWidth = Math.abs(xScale(endDate) - xScale(startDate));
                                
                                // Calculate scale to make one month fit the available width
                                const newScale = availableWidth / totalWidth;
                                
                                // Calculate the translation needed to center today
                                const centerTranslateX = -(todayX * newScale - availableWidth / 2);
                                
                                zoom.setTransformMatrix({
                                    scaleX: newScale,
                                    scaleY: 1,
                                    translateX: centerTranslateX,
                                    translateY: 0,
                                    skewX: 0,
                                    skewY: 0
                                });
                            }, [zoom, xScale, availableWidth]);

                            return (
                                <div className="flex flex-col h-full">
                                    {/* Controls */}
                                    <div className="flex flex-col space-y-4 p-4 border-b">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
                            {/* Navigation buttons on first row in mobile */}
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handlePrevious}
                                    className="flex items-center px-3 py-1 rounded bg-blue-500 text-white"
                                >
                                    <IoChevronBackOutline className="mr-1" />
                                    Previous
                                </button>
                                <button
                                    onClick={handleToday}
                                    className="px-3 py-1 rounded bg-gray-500 text-white flex items-center"
                                >
                                    <IoTodayOutline className="mr-1" />
                                    Today
                                </button>
                                <button
                                    onClick={handleNext}
                                    className="flex items-center px-3 py-1 rounded bg-blue-500 text-white"
                                >
                                    Next
                                    <IoChevronForwardOutline className="ml-1" />
                                </button>
                            </div>

                            {/* Zoom controls and date range on second row in mobile, right side in desktop */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                                    <button
                                        onClick={handleZoomOut}
                                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                        title="Zoom Out"
                                    >
                                        <IoRemoveOutline size={20} />
                                    </button>
                                    <span className="text-sm px-2">Zoom</span>
                                    <button
                                        onClick={handleZoomIn}
                                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                        title="Zoom In"
                                    >
                                        <IoAddOutline size={20} />
                                    </button>
                                </div>
                                
                                <div className="text-sm text-gray-500 ml-4">
                                    <Popover className="relative">
                                        <Popover.Button className="flex items-center px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                            <IoCalendarOutline className="mr-2" />
                                            {formatDateWithTimezone(visibleRange.start, 'MMM d, yyyy')} - {formatDateWithTimezone(visibleRange.end, 'MMM d, yyyy')}
                                        </Popover.Button>

                                        <Popover.Panel className="absolute z-50 mt-2 -translate-x-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
                                            {({ close }) => (
                                                <div>
                                                    <div className="mb-3 text-center">
                                                        <span className="font-medium">Select Date Range</span>
                                                    </div>
                                                    
                                                    {/* Simple date inputs instead of DayPicker component */}
                                                    <div className="flex flex-col gap-3 mb-4">
                                                        <div>
                                                            <label className="block text-sm font-medium mb-1">Start Date</label>
                                                            <input
                                                                type="date"
                                                                className="w-full p-2 rounded bg-gray-100 dark:bg-gray-700"
                                                                defaultValue={formatDateWithTimezone(visibleRange.start, 'yyyy-MM-dd')}
                                                                id="startDateInput"
                                                                aria-label="Start Date"
                                                                max="2999-12-31"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium mb-1">End Date</label>
                                                            <input
                                                                type="date"
                                                                className="w-full p-2 rounded bg-gray-100 dark:bg-gray-700"
                                                                defaultValue={formatDateWithTimezone(visibleRange.end, 'yyyy-MM-dd')}
                                                                id="endDateInput"
                                                                aria-label="End Date"
                                                                max="2999-12-31"
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mt-3 flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                // Get values from inputs directly
                                                                const startInput = document.getElementById('startDateInput') as HTMLInputElement;
                                                                const endInput = document.getElementById('endDateInput') as HTMLInputElement;
                                                                
                                                                if (!startInput || !endInput || !startInput.value) {
                                                                    close();
                                                                    return;
                                                                }
                                                                
                                                                console.log('Date inputs:', { 
                                                                    startValue: startInput.value, 
                                                                    endValue: endInput.value 
                                                                });
                                                                
                                                                // Create date objects with timezone correction
                                                                // We use YYYY-MM-DD format from the input and construct date objects
                                                                // to ensure local timezone and no timezone shift issues
                                                                const startDateStr = startInput.value; // Format: YYYY-MM-DD
                                                                const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
                                                                const startDate = new Date(startYear, startMonth - 1, startDay); // Month is 0-indexed
                                                                startDate.setHours(0, 0, 0, 0);
                                                                
                                                                let endDate;
                                                                if (endInput.value) {
                                                                    const endDateStr = endInput.value;
                                                                    const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number);
                                                                    endDate = new Date(endYear, endMonth - 1, endDay);
                                                                    endDate.setHours(23, 59, 59, 999);
                                                                } else {
                                                                    // Default to 2 weeks if no end date
                                                                    endDate = new Date(startDate);
                                                                    endDate.setDate(endDate.getDate() + 14);
                                                                    endDate.setHours(23, 59, 59, 999);
                                                                }
                                                                
                                                                console.log('Parsed dates:', { 
                                                                    startDate: startDate.toISOString(), 
                                                                    endDate: endDate.toISOString() 
                                                                });
                                                                
                                                                // Calculate translation to position the start date at the left edge
                                                                const startX = xScale(startDate);
                                                                const endX = xScale(endDate);
                                                                
                                                                // Calculate the new scale to fit the selected date range
                                                                const rangeWidth = Math.abs(endX - startX);
                                                                const newScale = rangeWidth > 0 
                                                                    ? (availableWidth * 0.9) / rangeWidth 
                                                                    : zoom.transformMatrix.scaleX;
                                                                
                                                                // Calculate translation to center the range
                                                                const newTranslateX = -startX * newScale;
                                                                
                                                                zoom.setTransformMatrix({
                                                                    ...zoom.transformMatrix,
                                                                    scaleX: newScale,
                                                                    translateX: newTranslateX,
                                                                    translateY: 0,
                                                                    scaleY: 1,
                                                                    skewX: 0,
                                                                    skewY: 0
                                                                });
                                                                
                                                                // Close the popover
                                                                close();
                                                            }}
                                                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                        >
                                                            Apply
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </Popover.Panel>
                                    </Popover>
                                </div>
                            </div>
                        </div>

                                        {/* Status Legend */}
                                        <div className="flex flex-wrap gap-2">
                                            {Object.entries(statusConfig)
                                                .filter(([status]) => 
                                                    applications.some(app => 
                                                        app.status === status || 
                                                        app.status_history?.some(history => history.status === status)
                                                    )
                                                )
                                                .map(([status, config]) => (
                                                <div
                                                    key={status}
                                                    className="flex items-center px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                >
                                                    <div
                                                        className="w-3 h-3 rounded mr-2"
                                                        style={{ backgroundColor: config.color }}
                                                    />
                                                    <span className="text-xs whitespace-nowrap">
                                                        {config.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="relative flex flex-1">
                        {/* Fixed company labels */}
                        <div 
                            className="shrink-0 z-10 bg-inherit border-r"
                            style={{ width: DETAILS_COLUMN_WIDTH, height: totalHeight }}
                        >
                            <div className="h-8 border-b" /> {/* Header spacer */}
                            {applications.map((app, index) => (
                                <div 
                                    key={`label-${app.id}`}
                                    className="px-4 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                    style={{ height: ROW_HEIGHT }}
                                    onClick={() => onApplicationClick(app)}
                                >
                                    <div className="w-full">
                                        <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'} truncate`}>
                                            {app.company}
                                        </div>
                                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} truncate`}>
                                            {app.job_title}
                                        </div>
                                        <div className="flex items-center mt-1 space-x-2">
                                            {app.location && (
                                                <span className="text-xs text-gray-500 flex items-center">
                                                    <IoLocationOutline className="mr-1" />
                                                    {app.location}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Timeline */}
                        <div className="relative flex-1">
                            {/* Fixed date header */}
                            <div 
                                className="absolute top-0 left-0 right-0 h-8 border-b z-10 bg-inherit"
                            />

                            <svg
                                width={availableWidth}
                                height={totalHeight}
                                className="cursor-grab active:cursor-grabbing"
                                ref={zoom.containerRef}
                                style={{ touchAction: 'none' }}
                                onMouseDown={handleDragStart}
                                onMouseMove={handleDragMove}
                                onMouseUp={handleDragEnd}
                                onMouseLeave={handleDragEnd}
                                onTouchStart={handleDragStart}
                                onTouchMove={handleDragMove}
                                onTouchEnd={handleDragEnd}
                            >
                                <defs>
                                    <clipPath id="timelineClip">
                                        <rect x="0" y={HEADER_HEIGHT} width={availableWidth} height={totalHeight - HEADER_HEIGHT} />
                                    </clipPath>
                                </defs>

                                <rect
                                    width={availableWidth}
                                    height={totalHeight}
                                    fill="transparent"
                                />
                                
                                {/* Date labels outside the zoom transformation and clipping */}
                                <g transform={`translate(${zoom.transformMatrix.translateX}, 0)`}>
                                    {renderGridLinesAndLabels(zoom.transformMatrix).labels}
                                </g>

                                {/* Grid lines and status bars with clipping */}
                                <g clipPath="url(#timelineClip)">
                                    <Group transform={zoom.toString()}>
                                        {renderGridLinesAndLabels(zoom.transformMatrix).lines}
                                        {applications.map((app, index) =>
                                            renderApplication(app, index, xScale, yScale)
                                        )}
                                    </Group>
                                </g>
                            </svg>
                        </div>
                    </div>
                </div>
            );
        }}
    </Zoom>
                </div>
            )}
        </div>
    );
};

export default JobSearchTimeline;